import { get, size, startsWith } from "lodash"
import createModule from "../createModule"
import view from "./view"
import { Creators } from "./model"
import { Creators as PortfolioCreators } from "modules/portfolio-root/model"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as DialogCreators } from "modules/dialog/model"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { generateClassificationQueryForContributors, getClassifications } from "helpers/classification"
import { getAssetsExposureFieldName, getAssetsFactorSensitivityFieldName } from "helpers/assets"
import { getColumnOrderOptions, getGroupingOptions, getIdSort, getMenuItems, getMetricTypeOptions, getSelectedMetricType, getSelectedSubView, getSubViewOptions } from "./selectors"
import { getAggregatedData, getColumnsByCategories, getCompositionStatus, getOrderedSecurities, getSecurities, getQueries } from "./selectors/data"
import { getDownloadData, getDownloadButtonTooltip } from "./selectors/download"
import { getGroupDropdown } from "./selectors/group"
import { getMissingDataFiles, betasAreMissing, betasQueryFragment } from "selectors/missingDataFiles"
import { getSelectedViewIsContentSet } from "selectors/contentSets"
import { getCurrency } from "modules/portfolio-root/selectors/portfolios"
import { FACTOR_SENSITIVITY_TYPES, getFactorSensitivityQueryParams } from "helpers/factorSensitivity"
import { getSelectedView } from "selectors/assets"
import withUrlParams from "hocs/withUrlParams"
import { MODEL_METADATA_QUERY } from "resources/models"

import "./styles.scss"

export default withUrlParams(createModule({
  view,
  externalProps: (state, props) => ({
    groupingOptions: getGroupingOptions(state, props),
    queries: getQueries(state, props),
    metricType: getSelectedMetricType(state, props),
    selectedSubView: getSelectedSubView(state, props),
    classifications: getClassifications(state),
    missingDataFiles: getMissingDataFiles(state, props),
    selectedView: getSelectedView(state, props),
    selectedViewIsContentSet: getSelectedViewIsContentSet(state, props),
    currency: getCurrency(state),
  }),
  required: ["accountId", "modelId", "portfolioId"],
  data: [{
    resource: "selectedContentSetCategories",
    as: null,
    url: ({ selectedView, selectedViewIsContentSet }) => {
      if (!selectedView || !selectedViewIsContentSet) {
        return null
      }
      const query = `query ($contentSetId: String!) {
        contentSet(id: $contentSetId){
          categories{
            id,
            name,
            factors{
              id
              name
            }
          }
        }
      }`

      return {
        id: `selectedContentSetCategories-${selectedView}`,
        query,
        variables: {
          contentSetId: selectedView,
        },
      }
    },
  }, {
    intradayChannel: ({ accountId, portfolioId }) => `${accountId}_${portfolioId}`,
    resource: "intradayComposition",
    url: ({ classifications, date, modelId, portfolio, segment, groupingOptions, queries, isExpanded }) => {
      if (!queries.composition || groupingOptions) {
        return null
      }

      return ({
        id: `intradayComposition-${modelId}${portfolio}${date}${JSON.stringify(segment)}${isExpanded}`,
        query: `query ($modelId: String!, $on: Date!, $portfolio: String, $segment: Segment, $expand: Boolean) {
          model(id: $modelId) {
            intradaySimulationBatch (
              positionSet: {
                type: PORTFOLIO
                id: $portfolio
                segment: $segment,
                expand: $expand
              }
              on: $on
            ) {
              composition(scaleFormat: PERCENT_EQUITY_GMV) {
                positionsCount
                gmv
                referenceEquity
                positions {
                  id
                  assetClass
                  description
                  isUnmodeled
                  modelProviderId
                  country
                  currency
                  economicExposure
                  positionPercentEquity: positionSetEconomicExposure
                  ${generateClassificationQueryForContributors("GICS", classifications)}
                  bloombergTicker
                  notHeld
                }
              }
            }
            nonActive: intradaySimulationBatch(
              positionSet: {
                type: PORTFOLIO
                id: $portfolio
                segment: $segment,
                expand: $expand
              }
              on: $on
            ) {
              composition {
                positions {
                  id
                  assetClass
                  modelProviderId
                  economicExposure
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: date,
          portfolio,
          segment,
          expand: isExpanded,
        },
      })
    },
  }, {
    intradayChannel: ({ accountId, portfolioId }) => `${accountId}_${portfolioId}`,
    resource: "intradayGroupedComposition",
    url: ({ modelId, date, portfolioId, groupingOptions, segment, classifications, queries, isExpanded }) => {
      if (!queries.composition || !groupingOptions) {
        return null
      }

      return {
        id: `intradayGroupedComposition-${modelId}${portfolioId}${date}${JSON.stringify(groupingOptions)}${JSON.stringify(segment)}${isExpanded}`,
        query: `query ($modelId: String!, $on: Date!, $portfolioId: String, $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $segment: Segment, $expand: Boolean) {
          model (id: $modelId) {
            intradaySimulationBatch (
              positionSet: {
                type: PORTFOLIO
                id: $portfolioId
                segment: $segment
                expand: $expand
              }
              on: $on
            ) {
              composition (scaleFormat: PERCENT_EQUITY_GMV) {
                gmv
                referenceEquity
                compositionBy (
                  groupBy: $groupBy
                  groupById: $groupById
                  classificationTier: $tier
                ) {
                  id
                  description
                  economicExposure
                  positionPercentEquity: positionSetEconomicExposure
                  contributors {
                    id
                    assetClass
                    country
                    currency
                    description
                    isUnmodeled
                    modelProviderId
                    economicExposure
                    positionPercentEquity: positionSetEconomicExposure
                    ${generateClassificationQueryForContributors("GICS", classifications)}
                    bloombergTicker
                    notHeld
                  }
                }
              }
            }
            nonActive: intradaySimulationBatch (
              positionSet: {
                type: PORTFOLIO
                id: $portfolioId
                segment: $segment
                expand: $expand
              }
              on: $on
            ) {
              composition {
                compositionBy(
                  groupBy: $groupBy
                  groupById: $groupById
                  classificationTier: $tier
                ) {
                  id
                  economicExposure
                  contributors {
                    id
                    assetClass
                    modelProviderId
                    economicExposure
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: date,
          portfolioId,
          segment,
          expand: isExpanded,
          ...groupingOptions,
        },
      }
    },
  }, {
    intradayChannel: ({ accountId, portfolioId }) => `${accountId}_${portfolioId}`,
    resource: "intradayRisk",
    url: ({ modelId, date, portfolioId, isOpPro, segment, queries, isExpanded, groupingOptions, currency }) => {
      if (!queries.risk || groupingOptions || isOpPro) {
        return null
      }

      return {
        id: `intradayRisk-${modelId}${portfolioId}${date}${isExpanded}${JSON.stringify(segment)}${currency}`,
        query: `query ($modelId: String!, $on: Date!, $portfolioId: String, $segment: Segment, $expand: Boolean) {
          model (id: $modelId) {
            intradaySimulationBatch (positionSet: {
              type: PORTFOLIO
              id: $portfolioId
              segment: $segment,
              expand: $expand
            },
            on: $on
            ) {
              risk {
                total
              }
              riskContributors {
                id
                modelProviderId
                assetClass
                notHeld
                total
                attribution{
                  summary{
                    factors
                    specific
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: date,
          portfolioId,
          segment,
          expand: isExpanded,
        },
      }
    },
  }, {
    intradayChannel: ({ accountId, portfolioId }) => `${accountId}_${portfolioId}`,
    resource: "intradayGroupedRisk",
    url: ({ modelId, date, portfolioId, isOpPro, groupingOptions, segment, queries, isExpanded, currency }) => {
      if (!queries.risk || !groupingOptions || isOpPro) {
        return null
      }

      return {
        id: `intradayGroupedRisk-${modelId}${portfolioId}${date}${isExpanded}${JSON.stringify(groupingOptions)}${JSON.stringify(segment)}${currency}`,
        query: `query ($modelId: String!, $on: Date!, $portfolioId: String, $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $segment: Segment, $expand: Boolean) {
          model (id: $modelId) {
            intradaySimulationBatch (positionSet: {type: PORTFOLIO, id: $portfolioId, segment: $segment, expand: $expand}, on: $on) {
              groupedRiskContributors (groupBy: $groupBy, groupById: $groupById, classificationTier: $tier) {
                id
                name
                attribution {
                  summary {
                    factors
                    specific
                  }
                }
                contributors {
                  id
                  modelProviderId
                  assetClass
                  notHeld
                  total
                  attribution {
                    summary {
                      factors
                      specific
                    }
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: date,
          portfolioId,
          segment,
          expand: isExpanded,
          ...groupingOptions,
        },
      }
    },
  }, {
    intradayChannel: ({ accountId, portfolioId }) => `${accountId}_${portfolioId}`,
    resource: "intradayBeta",
    url: ({ modelId, date, portfolioId, segment, queries, groupingOptions, isExpanded, missingDataFiles }) => {
      // missingDataFiles contains the ids of missing data per modelId
      // eg. { QES-US: ['predictedBeta'], ... }
      // because we are checking for existence of a fileId in the object, we need to wait for the missingDataFiles to actually be loaded to prevent a false negative
      if (!queries.beta || groupingOptions || !missingDataFiles || betasAreMissing(missingDataFiles, modelId)) {
        return null
      }

      return {
        id: `intradayBeta-${modelId}${portfolioId}${date}${isExpanded}${JSON.stringify(segment)}`,
        query: `query ($modelId: String!, $on: Date!, $portfolioId: String, $segment: Segment, $expand: Boolean) {
          model (id: $modelId) {
            intradaySimulationBatch (positionSet: {type: PORTFOLIO, id: $portfolioId, segment: $segment, expand: $expand}, on: $on) {
              betaContributors {
                contributors {
                  id
                  modelProviderId
                  assetClass
                  notHeld
                  ${betasQueryFragment(missingDataFiles)}
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: date,
          portfolioId,
          expand: isExpanded,
          segment,
        },
      }
    },
  }, {
    intradayChannel: ({ accountId, portfolioId }) => `${accountId}_${portfolioId}`,
    resource: "intradayGroupedBeta",
    url: ({ modelId, date, portfolioId, groupingOptions, segment, queries, isExpanded, missingDataFiles }) => {
      // missingDataFiles contains the ids of missing data per modelId
      // eg. { QES-US: ['predictedBeta'], ... }
      // because we are checking for existence of a fileId in the object, we need to wait for the missingDataFiles to actually be loaded to prevent a false negative
      if (!queries.beta || !groupingOptions || !missingDataFiles || betasAreMissing(missingDataFiles)) {
        return null
      }

      return {
        id: `intradayGroupedBeta-${modelId}${portfolioId}${date}${isExpanded}${JSON.stringify(groupingOptions)}${JSON.stringify(segment)}`,
        query: `query ($modelId: String!, $on: Date!, $portfolioId: String, $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $segment: Segment, $expand: Boolean) {
          model (id: $modelId) {
            intradaySimulationBatch (positionSet: {type: PORTFOLIO, id: $portfolioId, segment: $segment, expand: $expand}, on: $on) {
              groupedBetaContributors (groupBy: $groupBy, groupById: $groupById, classificationTier: $tier){
                groupedContributors {
                  name
                  id
                  ${betasQueryFragment(missingDataFiles, true)}
                  contributors {
                    id
                    modelProviderId
                    assetClass
                    notHeld
                    ${betasQueryFragment(missingDataFiles)}
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: date,
          portfolioId,
          segment,
          expand: isExpanded,
          ...groupingOptions,
        },
      }
    },
  }, {
    intradayChannel: ({ accountId, portfolioId }) => `${accountId}_${portfolioId}`,
    resource: "intradayExposure",
    url: ({ modelId, date, portfolio, segment, groupingOptions, queries, isExpanded, metricType, selectedView, selectedSubView, selectedViewIsContentSet }) => {
      if ((!queries.exposure && !selectedViewIsContentSet) || groupingOptions || (selectedViewIsContentSet && !selectedSubView)) {
        return null
      }

      const contentSetQueryVariable = !selectedViewIsContentSet ? { declaration: "", usage: "" } : {
        declaration: ", $contentSetId: String!",
        usage: "(contentSetId: $contentSetId)",
      }

      return ({
        id: `intradayExposure-${modelId}${portfolio}${date}${selectedView}${selectedSubView}${metricType}${JSON.stringify(segment)}${isExpanded}`,
        query: `query ($modelId: String!, $portfolio: String!, $on: Date!, $segment: Segment, $expand: Boolean, $selectedSubView: [String]!${contentSetQueryVariable.declaration}) {
        model(id: $modelId) {
          intradaySimulationBatch(
            positionSet: {
              type: PORTFOLIO
              id: $portfolio
              segment: $segment
              expand: $expand
            }
            on: $on
          ) {
            exposureContributors${contentSetQueryVariable.usage} {
              contributors {
                id
                assetClass
                modelProviderId
                factors(category: $selectedSubView) {
                  id
                  ${getAssetsExposureFieldName(metricType)}
                }
              }
            }
          }
        }
      }`,
        variables: {
          on: date,
          modelId,
          portfolio,
          segment,
          expand: isExpanded,
          selectedSubView,
          ...(selectedViewIsContentSet && { contentSetId: selectedView }),
        },
      })
    },
  }, {
    intradayChannel: ({ accountId, portfolioId }) => `${accountId}_${portfolioId}`,
    resource: "intradayGroupedExposure",
    url: ({ modelId, date, portfolioId, groupingOptions, queries, segment, isExpanded, metricType, selectedView, selectedSubView, selectedViewIsContentSet }) => {
      if ((!queries.exposure && !selectedViewIsContentSet) || !groupingOptions || (selectedViewIsContentSet && !selectedSubView)) {
        return null
      }

      const contentSetQueryVariable = !selectedViewIsContentSet ? { declaration: "", usage: "" } : {
        declaration: ", $contentSetId: String!",
        usage: "contentSetId: $contentSetId",
      }

      return {
        id: `intradayGroupedExposure-${modelId}${portfolioId}${date}${selectedView}${selectedSubView}${metricType}${JSON.stringify(groupingOptions)}${JSON.stringify(segment)}${isExpanded}`,
        query: `query ($modelId: String!, $portfolioId: String!, $on: Date!, $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $segment: Segment, $expand: Boolean, $selectedSubView: [String]!${contentSetQueryVariable.declaration}) {
          model (id: $modelId) {
            intradaySimulationBatch(
              positionSet: {
                type: PORTFOLIO
                id: $portfolioId
                segment: $segment
                expand: $expand
              }
              on: $on
            ) {
              groupedExposureContributors (
                groupBy: $groupBy
                groupById: $groupById
                classificationTier: $tier
                ${contentSetQueryVariable.usage}
              ) {
                groupedContributors {
                  id
                  name
                  factors(category: $selectedSubView) {
                    id
                    ${getAssetsExposureFieldName(metricType, true)}
                  }
                  contributors {
                    id
                    assetClass
                    modelProviderId
                    factors(category: $selectedSubView) {
                      id
                      ${getAssetsExposureFieldName(metricType)}
                    }
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: date,
          portfolioId,
          segment,
          expand: isExpanded,
          selectedSubView,
          ...groupingOptions,
          ...(selectedViewIsContentSet && { contentSetId: selectedView }),
        },
      }
    },
  }, {
    intradayChannel: ({ accountId, portfolioId }) => `${accountId}_${portfolioId}`,
    resource: "intradayRiskFactors",
    url: ({ modelId, date, portfolioId, groupingOptions, queries, segment, isExpanded, currency }) => {
      if (!queries.riskFactors || groupingOptions) {
        return null
      }

      return {
        id: `intradayRiskFactors-${modelId}${portfolioId}${date}${isExpanded}${JSON.stringify(segment)}${currency}`,
        query: `query ($modelId: String!, $portfolioId: String!, $on: Date!, $segment: Segment, $expand: Boolean) {
          model(id: $modelId) {
            intradaySimulationBatch(
              positionSet: {type: PORTFOLIO, id: $portfolioId, segment: $segment, expand: $expand}
              on: $on
            ) {
              riskContributors {
                id
                assetClass
                modelProviderId
                attribution {
                  factors {
                    id
                    name
                    category
                    value
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: date,
          portfolioId,
          segment,
          expand: isExpanded,
        },
      }
    },
  }, {
    intradayChannel: ({ accountId, portfolioId }) => `${accountId}_${portfolioId}`,
    resource: "intradayGroupedRiskFactors",
    url: ({ modelId, date, portfolioId, groupingOptions, queries, segment, isExpanded, currency }) => {
      if (!queries.riskFactors || !groupingOptions) {
        return null
      }

      return {
        id: `intradayGroupedRiskFactors-${modelId}${portfolioId}${date}${isExpanded}${JSON.stringify(groupingOptions)}${JSON.stringify(segment)}${currency}`,
        query: `query ($modelId: String!, $portfolioId: String!, $on: Date!, $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $segment: Segment, $expand: Boolean) {
          model(id: $modelId) {
            intradaySimulationBatch (
              positionSet: {type: PORTFOLIO, id: $portfolioId, segment: $segment, expand: $expand}
              on: $on
            ) {
              groupedRiskContributors(groupBy: $groupBy groupById: $groupById classificationTier: $tier) {
                id
                name
                attribution {
                  factors {
                    id
                    name
                    category
                    value
                  }
                }
                contributors {
                  id
                  assetClass
                  modelProviderId
                  attribution {
                    factors {
                      id
                      name
                      category
                      value
                    }
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: date,
          portfolioId,
          segment,
          expand: isExpanded,
          ...groupingOptions,
        },
      }
    },
  }, {
    intradayChannel: ({ accountId, portfolioId }) => `${accountId}_${portfolioId}`,
    resource: "intradayFactorSensitivity",
    url: ({ modelId, date, portfolioId, groupingOptions, queries, segment, metricType, isExpanded, currency, selectedView }) => {
      if (!queries.sensitivity || groupingOptions) {
        return null
      }
      const factorSensitivityType = startsWith(selectedView, "predicted") ? FACTOR_SENSITIVITY_TYPES.PREDICTED : FACTOR_SENSITIVITY_TYPES.HISTORICAL

      const query = `query ($modelId: String!, $portfolioId: String!, $date: Date!, $segment: Segment, $expand: Boolean, $factorSensitivityType: FactorSensitivityType!, $days: PositiveInt, $stdDev: PositiveFloat, $horizon: FactorSensitivityTimeHorizon!) {
        model(id: $modelId) {
          intradaySimulationBatch(
            positionSet: {type: PORTFOLIO, id: $portfolioId, segment: $segment, expand: $expand}
            on: $date
          ) {
            factorSensitivityContributors(type: $factorSensitivityType horizon: $horizon stdDev: $stdDev days: $days) {
              id
              assetClass
              modelProviderId
              factors {
                id
                name
                category
                ${getAssetsFactorSensitivityFieldName(metricType)}
              }
            }
          }
        }
      }`

      return {
        id: `intradayFactorSensitivity-${modelId}${date}${portfolioId}${isExpanded}${factorSensitivityType}${metricType}${JSON.stringify(segment)}${currency}`,
        query,
        variables: {
          modelId,
          date,
          portfolioId,
          segment,
          expand: isExpanded,
          ...getFactorSensitivityQueryParams(factorSensitivityType),
        },
      }
    },
  }, {
    intradayChannel: ({ accountId, portfolioId }) => `${accountId}_${portfolioId}`,
    resource: "intradayGroupedFactorSensitivity",
    url: ({ modelId, date, portfolioId, groupingOptions, queries, selectedView, segment, metricType, isExpanded, currency, selectedSubView }) => {
      if (!queries.sensitivity || !groupingOptions) {
        return null
      }
      const factorSensitivityType = startsWith(selectedView, "predicted") ? FACTOR_SENSITIVITY_TYPES.PREDICTED : FACTOR_SENSITIVITY_TYPES.HISTORICAL

      const query = `query ($modelId: String!, $portfolioId: String!, $date: Date!, $selectedSubView: [String]!, $segment: Segment, $expand: Boolean, $factorSensitivityType: FactorSensitivityType!, $days: PositiveInt, $stdDev: PositiveFloat, $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $horizon: FactorSensitivityTimeHorizon!) {
        model(id: $modelId) {
          intradaySimulationBatch(
            positionSet: {type: PORTFOLIO, id: $portfolioId, segment: $segment, expand: $expand}
            on: $date
          ) {
            groupedFactorSensitivityContributors(type: $factorSensitivityType horizon: $horizon stdDev: $stdDev days: $days groupBy: $groupBy groupById: $groupById classificationTier: $tier) {
              id
              name
              factors(category: $selectedSubView) {
                id
                name
                category
                ${getAssetsFactorSensitivityFieldName(metricType, true)}
              }
              contributors {
                id
                assetClass
                modelProviderId
                factors(category: $selectedSubView) {
                  id
                  name
                  category
                  ${getAssetsFactorSensitivityFieldName(metricType)}
                }
              }
            }
          }
        }
      }`

      return {
        id: `intradayGroupedFactorSensitivity-${modelId}${date}${portfolioId}${isExpanded}${factorSensitivityType}${metricType}${JSON.stringify(segment)}${currency}${JSON.stringify(groupingOptions)}${selectedSubView}`,
        query,
        variables: {
          modelId,
          date,
          portfolioId,
          selectedSubView,
          segment,
          expand: isExpanded,
          ...groupingOptions,
          ...getFactorSensitivityQueryParams(factorSensitivityType),
        },
      }
    },
  }, {
    intradayChannel: ({ accountId, portfolioId }) => `${accountId}_${portfolioId}`,
    resource: "intradayMarginalContribution",
    url: ({ modelId, date, portfolioId, isOpPro, isActive, segment, includeNotHeld, queries, expand, currency }) => {
      if (!queries.marginal || isOpPro) {
        return null
      }

      return {
        id: `${modelId}${date}${portfolioId}${expand}${isActive}${JSON.stringify(segment)}${includeNotHeld}${currency}`,
        query: `query (
            $modelId: String!
            $on: Date!
            $portfolioId: String!
            $segment: Segment
            $expand: Boolean
          ) {
          model (id: $modelId) {
            intradaySimulationBatch (positionSet: {type: PORTFOLIO, id: $portfolioId, segment: $segment, expand: $expand}, on: $on) {
              risk {
                total
              }
              riskContributors {
                id
                modelProviderId
                assetClass
                notHeld
                total
                exposureRiskCorrelation{
                  volatility
                  correlation
                  marginalContribution
                  riskContribution
                }
                attribution {
                  exposureRiskCorrelation{
                    factors{
                      volatility
                      correlation
                      marginalContribution
                      riskContribution
                    }
                    specific{
                      volatility
                      correlation
                      marginalContribution
                      riskContribution
                    }
                  }
                  summary {
                    factors
                    specific
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: date,
          portfolioId,
          segment,
          expand,
        },
      }
    },
  }, {
    resource: "intradayGroupedMarginalContribution",
    url: ({ modelId, date, portfolioId, isOpPro, isActive, groupingOptions, segment, includeNotHeld, queries, expand, currency }) => {
      if (!queries.marginal || !groupingOptions || isOpPro) {
        return null
      }

      return {
        id: `${modelId}${date}${portfolioId}${expand}${isActive}${JSON.stringify(groupingOptions)}${JSON.stringify(segment)}${includeNotHeld}${currency}`,
        query: `query (
            $modelId: String!
            $on: Date!
            $portfolioId: String!
            $groupBy: ContributorGroupType!
            $groupById: String
            $tier: String
            $segment: Segment
            $expand: Boolean
          ) {
          model (id: $modelId) {
            intradaySimulationBatch (positionSet: {type: PORTFOLIO, id: $portfolioId, segment: $segment, expand: $expand}, on: $on) {
              groupedRiskContributors(groupBy: $groupBy, groupById: $groupById, classificationTier: $tier) {
                id
                name
                exposureRiskCorrelation {
                  riskContribution
                }
                attribution {
                  exposureRiskCorrelation {
                    factors {
                      volatility
                      correlation
                      riskContribution
                    }
                    specific {
                      volatility
                      correlation
                      riskContribution
                    }
                  }
                }
                contributors {
                  id
                  modelProviderId
                  assetClass
                  notHeld
                  total
                  exposureRiskCorrelation {
                    volatility
                    correlation
                    marginalContribution
                    riskContribution
                  }
                  attribution {
                    exposureRiskCorrelation {
                      factors {
                        volatility
                        correlation
                        marginalContribution
                        riskContribution
                      }
                      specific {
                        volatility
                        correlation
                        marginalContribution
                        riskContribution
                      }
                    }
                  }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          on: date,
          portfolioId,
          segment,
          expand,
          ...groupingOptions,
        },
      }
    },
  },
  MODEL_METADATA_QUERY,
  ],
  actions: {
    ...Creators,
    openSecurityProfile: ProfileCreators.openSecurityProfile2,
    openFactorProfile: ProfileCreators.openFactorProfile,
    openDialog: DialogCreators.open,
    setExpansionMode: PortfolioCreators.setExpansionMode,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
  },
  mapStateToProps: (state, props) => ({
    ...get(state, "modules.intradayAssetTable"),
    aggregatedRow: getAggregatedData(state, props),
    columns: getColumnsByCategories(state, props),
    count: size(getOrderedSecurities(state, props)),
    foundSecurities: getSecurities(state, props),
    status: getCompositionStatus(state),
    groupDropdown: getGroupDropdown(state, props),
    idSort: getIdSort(state, props),
    downloadData: getDownloadData(state, props),
    downloadButtonTooltip: getDownloadButtonTooltip(state, props),
    selectedView: getSelectedView(state, props),
    metricTypeOptions: getMetricTypeOptions(state, props),
    menuItems: getMenuItems(state, props),
    subViewOptions: getSubViewOptions(state, props),
    subView: getSelectedSubView(state, props),
    columnOrderOptions: getColumnOrderOptions(state, props),
    columnOrder: state.modules.intradayAssetTable.columnOrder,
  }),
}))
